/**
 * useWordActions - Centralized word collection actions hook
 *
 * Provides a unified interface for collecting and uncollecting words
 * across the worksheet and sidebar. Handles metadata preservation, deduplication,
 * and reactivation of set-aside words.
 *
 * Scope 06: Rhyme-specific actions removed. All collection goes through
 * flat-word collectWord/uncollectWord. The grouping engine (Scope 04) handles
 * visual rhyme grouping — no parent-child storage needed.
 */

import { useCallback } from 'react'
import { useWordCollectionStore } from '../store/wordCollectionStore'
import {
  createWord,
  type DiscoveryMetadata,
} from '../types/wordCollection'

/**
 * Hook providing centralized word collection actions.
 *
 * Features:
 * - Collect words with discovery metadata
 * - Reactivate set-aside words without creating duplicates
 * - Remove words from collection
 * - Check collection status
 */
export function useWordActions() {
  const {
    currentSongId,
    addWord,
    deleteWord,
    changeWordStatus,
    words,
  } = useWordCollectionStore()

  /**
   * Collect a word into the active collection.
   *
   * Behavior:
   * - If word already exists and is set_aside, reactivates it
   * - Otherwise creates new word with provided metadata
   * - Preserves existing notes and discovery data when reactivating
   */
  const collectWord = useCallback(
    (wordText: string, provenance?: DiscoveryMetadata) => {
      if (!currentSongId) {
        console.warn('No current song ID - cannot collect word')
        return
      }

      // Check if word already exists (case-insensitive)
      const existingWord = words.find(w => w.word.toLowerCase() === wordText.toLowerCase())

      if (existingWord) {
        // Reactivate if set aside, otherwise already collected
        if (existingWord.status === 'set_aside') {
          changeWordStatus(existingWord.id, 'collected')
        }
        return
      }

      // Create new word with provided provenance or default to user brainstorm
      const newWord = createWord({
        id: crypto.randomUUID(),
        songId: currentSongId,
        word: wordText,
        status: 'collected',
        discovery: provenance || { method: 'user_brainstorm' }
      })

      addWord(newWord)
    },
    [currentSongId, addWord, changeWordStatus, words]
  )

  /**
   * Remove a word from the collection.
   *
   * Accepts either a word ID or word text (case-insensitive).
   *
   * @param wordIdOrText - The ID or text of the word to remove
   */
  const uncollectWord = useCallback(
    (wordIdOrText: string) => {
      // Check if this is a word ID (UUID format) or word text
      const wordById = words.find(w => w.id === wordIdOrText)

      if (wordById) {
        // It's an ID
        deleteWord(wordIdOrText)
        return
      }

      // Try to find by word text (case-insensitive)
      const wordByText = words.find(w => w.word.toLowerCase() === wordIdOrText.toLowerCase())

      if (wordByText) {
        deleteWord(wordByText.id)
      }
    },
    [deleteWord, words]
  )

  /**
   * Check if a word is currently in the collection.
   *
   * @param wordText - The word to check (case-insensitive)
   * @returns true if word is collected, false otherwise
   */
  const isCollected = useCallback(
    (wordText: string): boolean => {
      return words.some(w => w.word.toLowerCase() === wordText.toLowerCase())
    },
    [words]
  )

  return {
    collectWord,
    uncollectWord,
    isCollected,
  }
}
